import { useWallet } from '@solana/wallet-adapter-react';
import { Plus, Settings } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { usePreferences } from '../../contexts/PreferencesContext';
import { usePublisher } from '../../hooks/usePublisher';
import { cn } from '../../lib/utils';
import { hasConfiguredStorageProvider } from '../../utils/storageProviderUtils';
import { Button } from '../Button';
import { ImportDappDialog } from '../ImportDappDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { getAddDappGate } from './addDappGate';

interface AddDappActionsProps {
  isPublisherVerificationApproved: boolean;
  publisherVerificationMessage: string;
  addLabel?: string;
  importClassName?: string;
  /** Called after navigating (closes the mobile drawer / dropdown). */
  onNavigate?: () => void;
}

/**
 * "Add App" + "Import existing dApp" with the shared gating (wallet connected,
 * publisher not frozen, verification approved, storage provider configured).
 * Must render inside PreferencesProvider.
 */
export function AddDappActions({
  isPublisherVerificationApproved,
  publisherVerificationMessage,
  addLabel = 'Add App',
  importClassName,
  onNavigate,
}: AddDappActionsProps) {
  const navigate = useNavigate();
  const { connected } = useWallet();
  const { preferences } = usePreferences();
  const { activePublisher } = usePublisher();
  const [isImportOpen, setIsImportOpen] = useState(false);

  const gate = getAddDappGate({
    isWalletConnected: connected,
    isPublisherFrozen: activePublisher?.isFrozen === true,
    isPublisherVerificationApproved,
    publisherVerificationMessage,
    isStorageProviderConfigured: hasConfiguredStorageProvider(preferences),
  });

  const handleAdd = () => {
    navigate(
      gate.needsStorageProvider
        ? '/dashboard/settings/preferences'
        : '/dashboard/new-dapp',
    );
    onNavigate?.();
  };

  return (
    <div className="flex w-full flex-col items-center gap-1">
      <TooltipProvider>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <span className="block w-full">
              <Button
                variant="primary"
                className="w-full disabled:pointer-events-none"
                disabled={gate.isBlocked}
                onClick={handleAdd}
              >
                {gate.needsStorageProvider ? (
                  <Settings className="size-4" />
                ) : (
                  <Plus className="size-4" />
                )}
                {gate.needsStorageProvider ? 'Set a Provider' : addLabel}
              </Button>
            </span>
          </TooltipTrigger>
          {gate.reason && (
            <TooltipContent>
              <p>{gate.reason}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <Button
        variant="ghostMuted"
        className={cn('h-8 w-full', importClassName)}
        disabled={gate.isBlocked || gate.needsStorageProvider}
        onClick={() => setIsImportOpen(true)}
      >
        Import existing dApp
      </Button>
      {isImportOpen && (
        <ImportDappDialog
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
        />
      )}
    </div>
  );
}
